import type { OrganizationClaim } from '@bp/pim-auth-constants';
import { Button, DesktopIcon, ErrorState } from '@bp/ui-components';
import { useDocumentTitle } from '@uidotdev/usehooks';
import classNames from 'classnames';
import type React from 'react';
import { type FC, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { DesktopContext } from '../../context/IsDesktop';
import type { BpActionButton } from '../BpCard/BpCard';
import { BpHeader } from '../BpHeader/BpHeader';
import { Breadcrumb } from '../Breadcrumb/Breadcrumb';
import styles from './BpPage.module.scss';

type BpPageProps = {
  title: string;
  headline?: string | null;
  subheadline?: string | null;
  hideHeader?: boolean;
  hideBreadcrumbs?: boolean;
  breakHeader?: boolean;
  showBackButton?: boolean;
  fullWidth?: boolean;
  fullHeight?: boolean;
  noHeadlineMargin?: boolean;
  isForbidden: boolean;
  hideTitleIfForbidden?: boolean;
  organization?: OrganizationClaim;
  className?: string | undefined;
  children: React.ReactNode;
};

export const BpPage: FC<BpPageProps> = ({
  title,
  headline = null,
  subheadline = null,
  hideHeader,
  hideBreadcrumbs = false,
  breakHeader,
  showBackButton,
  fullWidth,
  fullHeight,
  noHeadlineMargin,
  isForbidden,
  hideTitleIfForbidden,
  organization,
  className,
  children,
}) => {
  const { t } = useTranslation();
  useDocumentTitle(`${!title ? t('app.title.full') : title}${organization ? ` - ${organization.name}` : ''}`);

  const classes = classNames(
    styles.page,
    { [styles['full-width']]: fullWidth, [styles['full-height']]: fullHeight },
    className,
  );

  const topClasses = classNames(styles.top, {
    [styles['no-header-margin']]: noHeadlineMargin,
  });

  const desktopContext = useContext(DesktopContext);
  const headerActions: BpActionButton[] = [];
  if (desktopContext.showForceDesktopSwitch && !desktopContext.isDesktop) {
    headerActions.push({
      callback: () => {
        desktopContext.toggleDesktopMode();
      },
      hierarchy: 'tertiary',
      icon: <DesktopIcon />,
      className: styles['desktop-switch-button'],
      title: t('common.toggleDesktopMode'),
    });
  }

  return isForbidden ? (
    <ErrorState type='forbidden' pageTitle={!hideTitleIfForbidden ? title : undefined} />
  ) : (
    <div className={classes}>
      <div className={topClasses}>
        {!hideBreadcrumbs && <Breadcrumb className={styles.crumbs} />}
        {(title || headline) && !hideHeader && (
          <BpHeader
            headline={!headline ? title : headline}
            subHeadline={subheadline ? subheadline : ''}
            size='l'
            noMargin
            showBackButton={showBackButton}
            breakLine={breakHeader}
            className={styles.header}
            actions={headerActions}
          />
        )}
      </div>
      <div className={styles.content}>{children}</div>
    </div>
  );
};
